import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Divider,
  CircularProgress,
  Grid,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { fget, fpost } from '../../../../axios/axiosCalls';
import DashboardCard from '../../../../components/shared/DashboardCard';
import toast from 'react-hot-toast';
import axios from 'axios';
import { formatDateToDDMMYYYY, priceFormattingSymbol } from '../../../../utils/utils';
import { useAuth } from '../../../../guards/jwt/JwtContext';
import {
  Home,
  Person,
  DateRange,
  Payment,
  CreditCard,
  ArrowBack,
  CalendarMonth,
  Hotel,
  Share as ShareIcon,
  Email,
  Phone,
  FiberManualRecord
} from '@mui/icons-material';
import PaymentTermsFull from '../../../../components/Terms';

const PaymentVerificationModal = ({ open }) => (
  <Dialog open={open} PaperProps={{ sx: { bgcolor: 'background.paper' } }}>
    <DialogContent sx={{ 
      textAlign: 'center', 
      p: 4,
      minWidth: 300
    }}>
      <CircularProgress sx={{ mb: 2 }} />
      <Typography>
        Verifying payment...
      </Typography>
    </DialogContent>
  </Dialog>
);

const Payments = () => {
  const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
const { user } = useAuth();
const location = useLocation();
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const [propertyData, setPropertyData] = useState({});
const [bookingData, setBookingData] = useState({
    booking: {
      bookingId: null,
      propertyName: '',
      bedSpace: '',
      startDate: '',
      endDate: '',
      orderId: '',
      payableAmount: {
        totalAmount: 0,
        processingFees: 0,
        totalBedRate: 0,
        bedRatePerMonth: '',
        duration: 0,
        chargePer: '',
        currency: '',
        paymentTerms: {
          registrationFees: 0,
          adminChargesSingle: 0
        }
      }
    }
  });
  const { id } = useParams();
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };
  const navigate = useNavigate();

  const selectedBed = bookingData?.booking?.beds?.[0];
  const matchingRoom = selectedBed?.property?.rooms?.find(room => 
    room.roomTitle === selectedBed.roomTitle
  );
  const handlePayment = async () => {
    setPaymentProcessing(true);
    try {
      const res = await loadRazorpayScript();
      
      if (!res) {
        toast.error('Razorpay SDK failed to load');
        return;
      }
  
      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY,
        amount: bookingData.booking.payableAmount.totalAmount * 100, // Amount in smallest currency unit
        currency: bookingData.booking.payableAmount.currency,
        //       amount: bookingData.fee * 100,
        // currency: "INR",
        name: "Autumn Rooms",
        description: `Booking Payment for ${bookingData.booking.beds[0]?.property?.propertyName}`,
        order_id: bookingData.booking.orderId,
        handler: async (response) => {
          try {
            setVerifyingPayment(true);
            const paymentVerify = await fpost({
              url: `/api/v2/payments/${id}/verify-payment`,
              data: {
                ...response,
                currency: bookingData.booking.payableAmount.currency,
                amount: bookingData.booking.payableAmount.totalAmount
              }
            });
            setVerifyingPayment(false);
            if(paymentVerify.data.status === 'Success') {
                const successParams = `amount=${bookingData.booking.payableAmount.totalAmount}&orderId=${response.razorpay_payment_id}&currency=${bookingData.booking.payableAmount.currency}`;
                
                const redirectUrl = user ? 
                  `${location.pathname}/success?${successParams}` : 
                  `${location.pathname}/success?${successParams}`;
                
                navigate(redirectUrl);
              }
          } catch (err) {
            setVerifyingPayment(false);
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: bookingData.name,
          email: bookingData.email,
          contact: bookingData.phone,
        },
        theme: {
          color: "#3f51b5",
        },
        notes: {
          currency: bookingData.booking.payableAmount.currency
        }
      };
  
      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      setVerifyingPayment(false);
      console.error(error);
      toast.error('Payment initialization failed');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleShareClick = (event) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const handleCopyLink = async () => {
    const shareUrl = `${window.location.origin}${import.meta.env.VITE_BASE_URL}payments/${id}/`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Payment link copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy link');
    }
    handleShareClose();
  };

  const handleSendLink = async () => {
    try {
      // Show loading toast
      const loadingToast = toast.loading('Sending payment link...');
      
      // Make API call
      const response = await fpost({
        url: `/api/v2/payments/${bookingData.booking.bookingId}/alert-payment`
      });
  
      // Handle response
      if (response.data.status === 'Success') {
        toast.success('Payment link sent successfully');
      } else {
        throw new Error(response.data.message || 'Failed to send payment link');
      }
  
      // Cleanup
      toast.dismiss(loadingToast);
      handleShareClose();
  
    } catch (error) {
      toast.error(error.message || 'Failed to send payment link');
      handleShareClose();
    }
  };

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        const response = await axios.post(`${BASE_URL}/api/v2/payments/${id}/`);
        if (response.data.status === 'Success') {
          setBookingData(response.data);
          setPropertyData(response.data.booking.beds[0]?.property);
        } else {
          throw new Error('Failed to fetch booking details');
        }
      } catch (error) {
        setBookingData({
            "status": "Success",
            "bookingId": 594,
            "name": "Joe Doe",
            "email": "mrjoe@gmail.com",
            "phone": "123456789",
            "propertyName": "Marpole Vancouver",
            "propertyId": 594,
            "slug": "marpole-vancouver~594",
            "town": "Marpole",
            "postCode": null,
            "bedType": "Shared",
            "propertyQuality": 1,
            "startDate": "2019-08-24",
            "endDate": "2019-08-24",
            "creationDate": "2019-08-24",
            "fee": "5"
          })
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

//   if (error) {
//     return (
//       <Box p={3}>
//         <Typography color="error">Error: {error}</Typography>
//       </Box>
//     );
//   }

return (
  <>
    <DashboardCard sx={{ overflow: 'auto',}}>
      {/* Main Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, }}>
          {user && (
                <IconButton 
                  onClick={() => navigate(-1)}
                  sx={{ color: 'primary.main' }}
                >
                  <ArrowBack />
                </IconButton>
              )}
          <Typography variant="h3" sx={{pl:user?0:3}}>
            Pay and Reserve Bedspace
          </Typography>
        </Box>
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center',}}>
          <Typography variant="h5">
            Booking for {bookingData?.booking?.customer?.name}
          </Typography>
          {/* <Typography className="dot" sx={{ color: 'text.secondary' }}>•</Typography>
          <Typography variant="h5" color="text.secondary">
            {bookingData.booking.payableAmount.duration} {bookingData.booking.payableAmount.chargePer}s
          </Typography> */}
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          color: 'text.secondary',
          gap: 1,
          '& .dot': {
            fontSize: '8px',
            margin: '0 8px'
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Email fontSize="small" />
            <Typography>{bookingData.booking.customer.email}</Typography>
          </Box>
          {/* <Typography className="dot">•</Typography> */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Phone fontSize="small" />
            <Typography>{bookingData.booking.customer.phone}</Typography>
          </Box>
        </Box>
      </Box>
      </Box>

      <Grid container spacing={3} sx={{ p: 3, position: 'relative' }}>
        {/* Left Column - Reservation Details */}
        <Grid item xs={12} md={8}>
          <DashboardCard sx={{ 
            p: 3, 
            mb: 3,
            height: '100%',
            overflow: 'auto'
          }}>
            <Typography variant="h5" gutterBottom>Reservation Details</Typography>
            <Grid container spacing={3} sx={{mt:1}}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <CalendarMonth color="primary" sx={{mt:1}}/>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Check-in</Typography>
                    <Typography variant="h6">
                      {formatDateToDDMMYYYY(bookingData.booking.startDate)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <CalendarMonth color="primary" sx={{mt:1}}/>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Check-out</Typography>
                    <Typography variant="h6">
                      {formatDateToDDMMYYYY(bookingData.booking.endDate)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Hotel color="primary" sx={{mt:1}}/>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Duration</Typography>
                    <Typography variant="h6">
                      {bookingData.booking.payableAmount.duration} {bookingData.booking.payableAmount.chargePer}s
                    </Typography>
                    {/* <Typography variant="body2" color="text.secondary">
                      {bookingData.booking.beds[0].roomTitle} - {bookingData.booking.beds[0].bedSpace}
                    </Typography> */}
                  </Box>
                </Box>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            {/* <Typography variant='h6' gutterBottom>Room Details</Typography>
            <Typography variant="h6" gutterBottom>
              {bookingData.booking.beds[0]?.bedSpace} • {bookingData.booking.beds[0]?.roomTitle}
            </Typography> */}

            
            <PaymentTermsFull propertyType={propertyData.managingType?.name} />
            {bookingData?.booking?.beds?.[0]?.property?.managingType?.code === 'one_time' ? (
                  <ListItem sx={{ py: 0.25 }}>
                    <ListItemText 
                      primary={`For all one-time properties, there will be a service charge of ${bookingData.booking.payableAmount.currency} ${bookingData?.booking?.beds?.[0]?.property?.paymentTerms?.serviceCharge || 0}`}
                      primaryTypographyProps={{ 
                        variant: 'body2',
                        fontWeight: "bold",
                        lineHeight: 1.8,
                        fontSize: '0.875rem'
                      }} 
                    />
                  </ListItem>
                ) : null}

                  <ListItem sx={{ py: 0.25 }}>
                  <ListItemText 
                    primary="This booking will be automatically cancelled if payment is not completed within 48 hours of booking creation."
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      color: 'warning.main',
                      fontSize: '0.875rem',
                      lineHeight: 1.8
                    }} 
                  />
                </ListItem>
            {/* External Links */}

          </DashboardCard>
        </Grid>

        {/* Right Column - Payment Details */}
        <Grid item xs={12} md={4}>
          <DashboardCard sx={{
            position: { md: 'sticky' },
            top: { md: '24px' },
            height: 'fit-content',
            // maxHeight: { md: 'calc(100vh - 30px)' },
            overflow: 'auto'
          }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6">{bookingData.booking.beds[0]?.property?.propertyName}</Typography>
              <Typography color="text.secondary" gutterBottom>
                {bookingData.booking.beds[0]?.property?.town} {bookingData.booking.beds[0]?.country?.name}
              </Typography>
              <Box 
                  sx={{ 
                    mt: 1,
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    
                  }}
                >
                  <Box>
                  <Typography 
                      variant="subtitle2" 
                      color="primary.main"
                      sx={{ 
                        fontSize: '0.86rem',
                        fontWeight: 600,
                        mb: 0.5
                      }}
                    >
                      Bed Type
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.primary"
                      sx={{ 
                        fontSize: '0.75rem',
                        fontWeight: 500 
                      }}
                    >
                      {matchingRoom?.description}
                    </Typography>
                    {/* <Typography variant="body2" color="text.secondary">
                      {selectedBed?.bedSpace}
                    </Typography> */}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* <CalendarMonth fontSize="small" color="action" /> */}
                    <Typography 
                      variant="caption" 
                      color="text.primary"
                      sx={{ 
                        fontSize: '0.75rem',
                        fontWeight: 500 
                      }}
                    >
                      {bookingData.booking.payableAmount.duration} {bookingData.booking.payableAmount.chargePer}s
                    </Typography>
                  </Box>
                </Box>
            </Box>

            <Typography variant="h6" gutterBottom>Price Details</Typography>

            {/* Price Breakdown */}
            <Box sx={{ my: 2 }}>
              {bookingData.booking.payableAmount.paymentTerms.breakdown.map((item, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  
                  p: 1,
                  '&:hover': { bgcolor: 'grey.50' }
                }}>
                  <Typography>{item.type}</Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'flex-end'
                  }}>
                    <Typography>
                      {priceFormattingSymbol(item.amount, bookingData.booking.payableAmount.currency)}
                    </Typography>
                    {/* {item.refundable && (
                      <Typography 
                        variant="caption" 
                        color="success.main"
                        sx={{ 
                          fontSize: '0.7rem',
                          mt: 0.25
                        }}
                      >
                        (Refundable)
                      </Typography>
                    )} */}
                  </Box>
                </Box>
              ))}
              
              {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, p: 1 }}>
                <Typography>Service Charge</Typography>
                <Typography>
                  {priceFormattingSymbol(bookingData.booking.payableAmount.paymentTerms.serviceCharge, bookingData.booking.payableAmount.currency)}
                </Typography>
              </Box> */}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ 
              bgcolor: 'primary.lighter',
              p: 2,
              borderRadius: 1,
              mb: 3
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="h6" color="primary.main">Total Amount</Typography>
                <Typography variant="h6" color="primary.main">
                  {priceFormattingSymbol(bookingData.booking.payableAmount.totalAmount, bookingData.booking.payableAmount.currency)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
            {user && bookingData?.booking?.paymentInfo?.paymentStatus !== 'success' && (
              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={handleShareClick}
                startIcon={<ShareIcon />}
                sx={{ py: 1.5, mb: 2 }}
              >
                Share Payment Link
              </Button>
            )}
            
            {bookingData?.booking?.paymentInfo?.paymentStatus === 'success' ? (
              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled
                sx={{
                  bgcolor: 'success.light',
                  color: 'success.dark',
                  '&.Mui-disabled': {
                    bgcolor: 'success.light',
                    color: 'success.dark',
                  }
                }}
              >
                Already Paid
              </Button>
            ) : (
              <Button
                variant="contained"
                fullWidth
                size="large" 
                onClick={handlePayment}
                disabled={paymentProcessing}
                sx={{ py: 1.5 }}
              >
                {paymentProcessing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Pay ${priceFormattingSymbol(bookingData.booking.payableAmount.totalAmount, bookingData.booking.payableAmount.currency)}`
                )}
              </Button>
            )}
          </Box>

            <Menu
              anchorEl={shareAnchorEl}
              open={Boolean(shareAnchorEl)}
              onClose={handleShareClose}
            >
              <MenuItem onClick={handleCopyLink}>Copy Link</MenuItem>
              {navigator.share && 
                <MenuItem 
                  onClick={handleSendLink}
                  disabled={!bookingData.booking.bookingId}
                >
                  Send Link to Customer
                </MenuItem>
              }
            </Menu>
          </DashboardCard>
        </Grid>
      </Grid>
    </DashboardCard>
    <PaymentVerificationModal open={verifyingPayment} />
  </>
);
};

export default Payments;